import React, { ReactElement, useEffect } from "react";
import fs from "fs";
import path from "path";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";

import Layout from "@/components/layout";
import TradingHomeDesktop, { TradingHomeMobile, TradingHomeTablet } from "@/components/Home/Home";
import { DataTables } from "@/components/DataTables/DataTables";
import { useDevice } from "@/redux/selectors/deviceSelector";
import { DevicePlatform } from "@/redux/interfaces/IDevice";
import { useVhHandler } from "@/hooks/trade/useVhHandler";
import { useFontSizeChange } from "@/hooks/common/useFontSizeChange";
import {
    SYMBOLS_IMAGES_MAP_STORAGE_KEY,
    TIP_RANKS_ASSETS_LIST,
    WEB_TRADER_ASSETS_KEYS,
} from "@/constants/index";
import { NotificationsPopup } from "../components/TradeAlerts";

const HomeDesktop = () => {
    useEffect(() => {
        if (navigator.userAgent.indexOf("Chrome") !== -1) {
            document.body.classList.add("chrome");
            document.body.classList.remove("not-chrome");
        } else {
            document.body.classList.add("not-chrome");
            document.body.classList.remove("chrome");
        }

        return () => {
            document.body.classList.remove("chrome");
            document.body.classList.remove("not-chrome");
        };
    }, []);
    return (
        <>
            <TradingHomeDesktop />
            <DataTables />
            <NotificationsPopup />
        </>
    );
};

// TODO: tablet home components
const HomeTablet = () => {
    return <TradingHomeTablet />;
};

const HomeMobile = () => {
    return (
        <>
            <TradingHomeMobile />
        </>
    );
};

const Home = (props): ReactElement => {
    const { assetsMap, tipRanksAssetsMap } = props;
    const { platform: devicePlatform } = useDevice();

    useEffect(() => {
        if (assetsMap) {
            localStorage.setItem(SYMBOLS_IMAGES_MAP_STORAGE_KEY, JSON.stringify(assetsMap));
        }
        if (tipRanksAssetsMap) {
            localStorage.setItem(TIP_RANKS_ASSETS_LIST, JSON.stringify(tipRanksAssetsMap));
        }
    }, []);

    useVhHandler();
    useFontSizeChange();

    return (
        <Layout>
            {devicePlatform === DevicePlatform.Desktop ? (
                <HomeDesktop />
            ) : devicePlatform === DevicePlatform.Tablet ? (
                <HomeTablet />
            ) : devicePlatform === DevicePlatform.Mobile ? (
                <HomeMobile />
            ) : null}
        </Layout>
    );
};

function getBrandFromHost(host?: string): string | null {
    if (!host) return null;

    const cleanHost = host.split(":")[0];
    const parts = cleanHost.split(".");

    if (parts.length < 2) return null;

    return parts[parts.length - 2];
}

export async function getServerSideProps({ locale, req }) {
    const host = req.headers.host || "";
    const brand = getBrandFromHost(host) || "inefex";

    const hostname = host.split(":")[0];
    let tld = hostname.split(".").pop();
    if (!tld || tld === "localhost") {
        tld = "org";
    }

    let assetsMap;
    try {
        const response = await fetch(`https://webtrader-cdn.${brand}.${tld}/symbols/assets.json`, {
            method: "GET",
            headers: {
                "Cache-Control": "public, max-age=3600",
            },
        });
        assetsMap = await response.json();
    } catch {
        try {
            const fallbackResponse = await fetch("https://webtrader-cdn.market10.net/symbols/assets.json", {
                method: "GET",
                headers: {
                    "Cache-Control": "public, max-age=3600",
                },
            });
            assetsMap = await fallbackResponse.json();
        } catch {
            try {
                const localPath = path.join(process.cwd(), "public", "fo", "assets.json");
                const localData = fs.readFileSync(localPath, "utf-8");
                assetsMap = JSON.parse(localData);
            } catch {
                assetsMap = undefined;
            }
        }
    }

    const tipRanksAssets = async (): Promise<Record<string, string>> => {
        try {
            const response = await fetch(
                `${process.env.NEXT_PUBLIC_TIP_RANKS_BASE_URL}/stocks/tickers`,
                {
                    method: "GET",
                    headers: {
                        "X-APIKey": process.env.NEXT_PUBLIC_TIP_RANKS_API_KEY || "",
                        "X-APIToken": process.env.NEXT_PUBLIC_TIP_RANKS_API_TOKEN || "",
                        "Cache-Control": "public, max-age=3600",
                    },
                }
            );

            // Validate response status
            if (!response.ok) {
                console.error(
                    "[tipRanksAssetsMap] Non-OK response status:",
                    response.status,
                    response.statusText
                );
                return {};
            }

            // Validate content-type
            const contentType = response.headers.get("content-type");
            if (!contentType || !contentType.includes("application/json")) {
                console.error("[tipRanksAssetsMap] Response is not JSON. content-type:", contentType);
                return {};
            }

            const { tickers = [] } = await response.json();

            if (!Array.isArray(tickers) || tickers.length === 0) {
                return {};
            }

            const result = Object.entries(WEB_TRADER_ASSETS_KEYS).reduce(
                (acc, [internalKey, externalTicker]) => {
                    if (tickers.includes(externalTicker)) {
                        acc[internalKey] = externalTicker;
                    }
                    return acc;
                },
                {} as Record<string, string>
            );

            return result;
        } catch (e) {
            console.error("[tipRanksAssetsMap] Failed to load tickers:", e);
            return {};
        }
    };

    if (!assetsMap) {
        assetsMap = {};
    }

    const tipRanksAssetsMap = await tipRanksAssets();

    return {
        props: {
            assetsMap,
            tipRanksAssetsMap,
            ...(await serverSideTranslations(locale, ["common"])),
            revalidate: 10,
        },
    };
}

export default React.memo(Home);
